import { AppColors } from "@/styles/colors";
import {
  Button,
  Flex,
  Grid,
  HStack,
  Text,
} from "@chakra-ui/react";
import { Check, Handshake, X } from "lucide-react";

import "@/components/common/section-components/card/text-elements.css";
import { Control, FieldValues, UseFormClearErrors } from "react-hook-form";
import { Dispatch, SetStateAction } from "react";
import { useRoles } from "@/hooks/useRoles";
import { PermissionCode } from "@/constants/permissions";
import { FutureContractStatus } from "@/constants/entity-status";
import { FutureContractDetail } from "@/pages/grain-requests/future-contract/schema/future-contract.schema";
import { TonCard } from "./elements/FCTonCard";
import { DeliveryMonthCard } from "./elements/FCDeliveryMonthCard";
import { AmountCard } from "./elements/FCAmountCard";
import { FinalPriceCard } from "./elements/FCFinalPriceCard";

interface FutureContractActionsProps {
  futureContract?: FutureContractDetail | null;
  cantOperateFutureContract: boolean;
  loadingFutureContract?: boolean;
  loadingExecuteFutureContract?: boolean;
  loadingRejectFutureContract?: boolean;
  loadingSendNegotiation: boolean;
  loadingSendCommunication?: boolean;
  handleSendNegotiation?: () => void;
  handleExecuteFutureContract?: () => void;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
  setOpenInfoModal: (open: boolean) => void;
}

const FutureContractActions = ({
  futureContract,
  cantOperateFutureContract,
  loadingFutureContract,
  loadingExecuteFutureContract,
  loadingRejectFutureContract,
  loadingSendNegotiation,
  loadingSendCommunication,
  handleSendNegotiation,
  handleExecuteFutureContract,
  setOpenRejectModal,
  setOpenInfoModal,
}: FutureContractActionsProps) => {
  const executeDisabled =
    loadingFutureContract ||
    loadingRejectFutureContract ||
    loadingSendNegotiation ||
    loadingSendCommunication ||
    loadingExecuteFutureContract ||
    cantOperateFutureContract;

  const communicationDisabled =
    loadingFutureContract ||
    loadingRejectFutureContract ||
    loadingSendCommunication ||
    loadingSendNegotiation ||
    loadingExecuteFutureContract ||
    Number(futureContract?.mensajes?.length) >= 5 ||
    futureContract?.estado === FutureContractStatus.REJECTED ||
    futureContract?.estado === FutureContractStatus.FINISHED ||
    (futureContract?.estado !== FutureContractStatus.NEGOTIATION &&
      futureContract?.estado !== FutureContractStatus.IN_COMMUNICATION);

  const negotiationDisabled =
    loadingFutureContract ||
    loadingRejectFutureContract ||
    loadingSendCommunication ||
    loadingSendNegotiation ||
    loadingExecuteFutureContract ||
    cantOperateFutureContract ||
    futureContract?.estado === FutureContractStatus.NEGOTIATION;

  const rejectDisabled =
    loadingFutureContract ||
    loadingRejectFutureContract ||
    loadingExecuteFutureContract ||
    cantOperateFutureContract;

  return (
    <HStack
      ml="auto"
      mt={{ base: 4, md: 0 }}
      paddingTop="10px"
      css={{
        "@media (max-width: 1350px) and (min-width: 1272px)": {
          "& button": {
            paddingInline: "4px",
            paddingBlock: "2px",
          },
          "& button svg": {
            width: "14px",
            height: "14px",
          },
        },
        "@media (max-width: 1590px) and (min-width: 1351px)": {
          "& button": {
            paddingInline: "10px",
            paddingBlock: "4px",
          },
          "& button svg": {
            width: "16px",
            height: "16px",
          },
        },
        "@media (min-width: 1591px)": {
          "& button": {
            paddingInline: "12px",
            paddingBlock: "6px",
          },
          "& button svg": {
            width: "18px",
            height: "18px",
          },
        },
      }}
    >
      {
        futureContract?.estado !== FutureContractStatus.TO_PROCESS && (
          <Button
            disabled={executeDisabled}
            bgColor={AppColors.LIGHT_GREEN}
            onClick={handleExecuteFutureContract}
            loading={loadingExecuteFutureContract}
            data-testid="future-contract-execute-btn"
          >
            <Check />
            <Text
              fontWeight={600}
              css={
                {
                  "@media (max-width: 1350px) and (min-width: 1272px)": {
                    fontSize: "10px",
                  },
                  "@media (max-width: 1590px) and (min-width: 1351px)": {
                    fontSize: "12px",
                  },
                  "@media (min-width: 1591px)": {
                    fontSize: "16px",
                  },
                } as any
              }
            >
              Confirmar
            </Text>
          </Button>
        )}

      {(futureContract?.estado === FutureContractStatus.NEGOTIATION ||
        futureContract?.estado === FutureContractStatus.IN_COMMUNICATION) && (
        <Button
          bgColor={AppColors.BLACK_TEXT_SECONDARY}
          disabled={communicationDisabled}
          onClick={() => setOpenInfoModal(true)}
          loading={loadingSendCommunication}
          data-testid="future-contract-communication-btn"
        >
          <Handshake />
          <Text
            fontWeight={600}
            css={{
              "@media (max-width: 1350px) and (min-width: 1272px)": {
                fontSize: "10px",
              },
              "@media (max-width: 1590px) and (min-width: 1351px)": {
                fontSize: "12px",
              },
              "@media (min-width: 1591px)": {
                fontSize: "16px",
              },
            }}
          >
            Comunicación
          </Text>
        </Button>
      )}

      {futureContract?.estado !== FutureContractStatus.IN_COMMUNICATION &&
        futureContract?.estado !== FutureContractStatus.NEGOTIATION && (
          <Button
            bgColor={AppColors.BLACK_TEXT_SECONDARY}
            disabled={negotiationDisabled}
            onClick={handleSendNegotiation}
            loading={loadingSendNegotiation}
            data-testid="future-contract-negotiate-btn"
          >
            <Handshake />
            <Text
              fontWeight={600}
              css={{
                "@media (max-width: 1350px) and (min-width: 1272px)": {
                  fontSize: "10px",
                },
                "@media (max-width: 1590px) and (min-width: 1351px)": {
                  fontSize: "12px",
                },
                "@media (min-width: 1591px)": {
                  fontSize: "16px",
                },
              }}
            >
              Negociación
            </Text>
          </Button>
        )}

      <Button
        disabled={rejectDisabled}
        bgColor={AppColors.RED_ICON}
        onClick={() => setOpenRejectModal(true)}
        data-testid="future-contract-reject-btn"
      >
        <X />
        <Text
          fontWeight={600}
          css={{
            "@media (max-width: 1350px) and (min-width: 1272px)": {
              fontSize: "10px",
            },
            "@media (max-width: 1590px) and (min-width: 1351px)": {
              fontSize: "12px",
            },
            "@media (min-width: 1591px)": {
              fontSize: "16px",
            },
          }}
        >
          Rechazar
        </Text>
      </Button>
    </HStack>
  );
};

interface Props {
  futureContract?: FutureContractDetail | null;
  isEditingAmount?: boolean;
  isEditingTon?: boolean;
  isEditingDeliveryMonth?: boolean;
  loadingEditTon?: boolean;
  loadingEditDeliveryMonth?: boolean;
  loadingEditAmount?: boolean;
  handleEditAmount?: () => void;
  handleEditTon?: () => void;
  handleEditDeliveryMonth?: () => void;
  setIsEditingAmount: Dispatch<SetStateAction<boolean>>;
  setIsEditingTon: Dispatch<SetStateAction<boolean>>;
  setIsEditingDeliveryMonth: Dispatch<SetStateAction<boolean>>;
  errors?: any;
  control: Control<FieldValues>;
  loadingFutureContract?: boolean;
  loadingExecuteFutureContract?: boolean;
  loadingRejectFutureContract?: boolean;
  handleExecuteFutureContract?: () => void;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
  clearErrors: UseFormClearErrors<FieldValues>
  setValue: (name: string, value: any) => void;
  setOpenInfoModal: (open: boolean) => void;
  loadingSendNegotiation: boolean;
  loadingSendCommunication?: boolean;
  handleSendNegotiation?: () => void;
}






export const FutureContractAmountAndActions = ({
  futureContract,
  isEditingAmount,
  loadingEditAmount,
  handleEditAmount,
  errors,
  control,
  setIsEditingAmount,
  loadingFutureContract,
  loadingExecuteFutureContract,
  loadingRejectFutureContract,
  handleExecuteFutureContract,
  setOpenRejectModal,
  clearErrors,
  setValue,
  setOpenInfoModal,
  loadingSendNegotiation,
  loadingSendCommunication,
  handleSendNegotiation,
  setIsEditingDeliveryMonth,
  setIsEditingTon,
  handleEditDeliveryMonth,
  handleEditTon,
  isEditingDeliveryMonth,
  isEditingTon,
  loadingEditDeliveryMonth,
  loadingEditTon,
}: Props) => {
  const { userPermissions } = useRoles();

  const cantOperateFutureContract =
    futureContract?.estado == FutureContractStatus.FINISHED ||
    futureContract?.estado == FutureContractStatus.CONFIRMED ||
    futureContract?.estado == FutureContractStatus.REJECTED;

  const hasWriteAccess = Boolean(
    userPermissions?.some(
      (p) =>
        p.codigo === PermissionCode.GRAIN_FUTURE_CONTRACT &&
        p.tipoPermiso === "W",
    ),
  );

  const canShowActions = hasWriteAccess && !cantOperateFutureContract;

  return (
    <Flex justifyContent="space-between" flexWrap={"wrap"}>
      <Grid
        width="fit-content"
        templateColumns={
          cantOperateFutureContract
            ? "repeat(4, max-content)"
            : "repeat(3, max-content)"
        }
        gap="36px"
      >
        <AmountCard
          futureContract={futureContract}
          isEditingAmount={isEditingAmount}
          loadingEditAmount={loadingEditAmount}
          handleEditAmount={handleEditAmount}
          errors={errors}
          control={control}
          setIsEditingAmount={setIsEditingAmount}
          clearErrors={clearErrors}
          setValue={setValue}
          cantOperateFutureContract={cantOperateFutureContract}
          hasWriteAccess={hasWriteAccess}
        />
        <FinalPriceCard futureContract={futureContract} />
        <TonCard
          futureContract={futureContract}
          isEditingTon={isEditingTon}
          loadingEditTon={loadingEditTon}
          handleEditTon={handleEditTon}
          errors={errors}
          control={control}
          setIsEditingTon={setIsEditingTon}
          clearErrors={clearErrors}
          setValue={setValue}
          cantOperateFutureContract={cantOperateFutureContract}
          hasWriteAccess={hasWriteAccess}
        />
        <DeliveryMonthCard
          futureContract={futureContract}
          isEditingDeliveryMonth={isEditingDeliveryMonth}
          loadingEditDeliveryMonth={loadingEditDeliveryMonth}
          handleEditDeliveryMonth={handleEditDeliveryMonth}
          errors={errors}
          control={control}
          setIsEditingDeliveryMonth={setIsEditingDeliveryMonth}
          clearErrors={clearErrors}
          setValue={setValue}
          cantOperateFutureContract={cantOperateFutureContract}
          hasWriteAccess={hasWriteAccess}
        />
      </Grid>
      {canShowActions && (
        <FutureContractActions
          futureContract={futureContract}
          cantOperateFutureContract={cantOperateFutureContract}
          loadingFutureContract={loadingFutureContract}
          loadingExecuteFutureContract={loadingExecuteFutureContract}
          loadingRejectFutureContract={loadingRejectFutureContract}
          loadingSendNegotiation={loadingSendNegotiation}
          loadingSendCommunication={loadingSendCommunication}
          handleSendNegotiation={handleSendNegotiation}
          handleExecuteFutureContract={handleExecuteFutureContract}
          setOpenRejectModal={setOpenRejectModal}
          setOpenInfoModal={setOpenInfoModal}
        />
      )}
    </Flex>
  );
};
